import React, { useRef, useState } from 'react';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Alert,
  Button,
} from '@mui/material';
import {
  Apps as AppsIcon,
  Info as InfoIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import CustomApplicationConfig from '../components/settings/CustomApplicationConfig';
import { isCustomerSubdomain, getCustomerIdFromSubdomain } from '../utils/constants';
import { pageVariants, sectionVariants } from '../animations/variants';
import { useReadOnlyMode } from '../hooks/useReadOnlyMode';
import { ReadOnlyButton } from '../components/common/ReadOnlyButton';

const CustomApplicationsPage = () => {
  const isCustomerContext = isCustomerSubdomain();
  const customerId = getCustomerIdFromSubdomain();
  const configRef = useRef(null);
  const [isDetailView, setIsDetailView] = useState(false);
  const isReadOnly = useReadOnlyMode();

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{ minHeight: '100vh', padding: '32px', backgroundColor: '#F9FBFD' }}
    >
      {/* Page Header - hidden in detail view */}
      {!isDetailView && (
        <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            border: '1px solid #E5E5E5',
            p: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  backgroundColor: '#27AE60',
                  width: 48,
                  height: 48,
                }}
              >
                <AppsIcon sx={{ fontSize: 24 }} />
              </Avatar>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: 'Bitter, Georgia, serif',
                    fontWeight: 600,
                    fontSize: '1.75rem',
                    color: '#253E66',
                  }}
                >
                  Custom SAML Applications
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.875rem',
                    color: '#999999',
                    fontWeight: 500,
                  }}
                >
                  Configure custom SAML applications for your organization
                </Typography>
              </Box>
            </Box>
            <ReadOnlyButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => configRef.current?.openAddDialog()}
              disabled={isReadOnly}
              sx={{
                backgroundColor: '#27AE60',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '6px',
                '&:hover': { backgroundColor: '#219A52' },
              }}
            >
              Add Application
            </ReadOnlyButton>
          </Box>
        </Paper>
        </motion.div>
      )}

      {/* Info Alert - hidden in detail view */}
      {!isDetailView && (
        <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
        <Alert
          severity="info"
          icon={<InfoIcon />}
          sx={{
            mb: 3,
            backgroundColor: '#EBF5FB',
            border: '1px solid #4698D320',
            borderRadius: '8px',
            '& .MuiAlert-icon': { color: '#4698D3' },
            '& .MuiAlert-message': {
              fontFamily: 'Metropolis, sans-serif',
              fontSize: '0.875rem',
              color: '#253E66',
            },
          }}
        >
          Custom SAML Applications allow you to integrate third-party applications that support SAML authentication.
          {isCustomerContext
            ? ' Configure applications specific to your customer realm.'
            : ' These configurations are global for your organization.'
          }
        </Alert>
        </motion.div>
      )}

      {/* Custom Applications Configuration Component */}
      <motion.div variants={sectionVariants}>
        <CustomApplicationConfig ref={configRef} onDetailViewChange={setIsDetailView} />
      </motion.div>
    </motion.div>
  );
};

export default CustomApplicationsPage;
